<style lang="scss">
.kiosk-print-page {
  min-height: 75vh;
  .kiosk-item-name {
    min-width: 180px;
  }
}
</style>

<template>
  <b-card>
    <h2 cy="page-title" class="font-weight-bolder mb-2 full-width">
      Kiosk Items Ordered
    </h2>
    <router-link v-if="!printing" :to="{ name: 'kiosk' }" class="d-inline-block mb-2">
      &laquo; Back to kiosk
    </router-link>

    <section v-if="!loading">
      <section v-if="prison">

        <!--Selectors-->
        <b-row v-if="!printing">
          <b-col cols="3">
            <label for="type-selector">Select Calls or Social Visits</label>
            <b-form-select v-model="isSocialVisits" @change="clearFilters(true)">
              <b-form-select-option :value="false">
                Video Calls
              </b-form-select-option>
              <b-form-select-option :value="true">
                Social Visits
              </b-form-select-option>
            </b-form-select>
          </b-col>
          <b-col v-if="isSocialVisits" cols="3">
            <label>
              Visit Location
            </label>
            <b-form-select
              v-model="selectedVisitLocationId"
              :options="visitLocationOptions"
              placeholder="Select location"
              @change="clearFilters(false)"
            />
          </b-col>
          <b-col cols="3">
            <label>Select a date</label>
            <b-form-datepicker
              v-model="date" :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
              close-button
              cy="date"
              reset-button
              today-button
              @input="getItems()"
            />
          </b-col>
        </b-row>

        <!--Buttons-->
        <b-row v-if="!printing" class="mt-2">
          <b-col v-if="date" cols="3">
            <b-btn variant="primary" class="full-width" @click="getItems()">
              <i class="fa fa-sync" style="margin-right: 5px" /> Refresh Items
            </b-btn>
          </b-col>
          <b-col v-if="date" cols="3">
            <b-btn
              v-if="date"
              :class="{'disabled': filteredItems.length <= 0 || !itemsAvailableToPrint}"
              variant="success"
              class="full-width"
              @click="print()"
            >
              <span class="fa fa-print" style="margin-right: 5px" /> Print Items
            </b-btn>
          </b-col>
        </b-row>

        <!--Print section-->
        <b-row class="mt-2">
          <b-col v-if="date" cols="12">
            <section v-for="call in filteredItems" :key="call._id" class="mb-2">
              <h3>{{ call.prisoner.firstname }} {{ call.prisoner.lastname }} ({{ call.prisoner.prisoner_number }}) -
                {{ call.visitor.firstname }} {{ call.visitor.lastname }}
              </h3>
              <p v-if="isSocialVisits">
                Location:
                <b>{{ call.visit_location.name }}</b>
              </p>
              <p class="mb-0 font-weight-bold">Slot Time: {{ call.booked_slot.slot_begin }} - {{ call.booked_slot.slot_end }}
                <span v-if="call.booked_slot.wing_name" class="d-inline-block"> on {{ call.booked_slot.wing_name }}</span>
              </p>
              <ul class="pl-1 pt-1">
                <li v-for="(item, i) in call.kiosk_items" :key="i" class="m-0">
                  <span class="d-inline-block kiosk-item-name mr-1">{{ item.name | capEachWord }}</span>x{{ item.amount }}
                </li>
              </ul>
            </section>
            <section v-if="filteredItems.length === 0" class="mb-2">
              <h4>No Kiosk Items for calls today</h4>
            </section>
          </b-col>
        </b-row>
      </section>

      <section v-else>
        <p>There was a problem getting your {{ isSocialVisits ? 'visit' : 'call' }} data, please refresh and try again</p>
      </section>
    </section>

    <b-spinner v-else variant="primary" label="Loading call" />
  </b-card>
</template>

<script>
import { mapState } from 'vuex';
import VisitService from '@/services/VisitService';
import moment from 'moment';
import { convertYearDayToDate, getVisitBookedForDate } from '@/util/helpers';
import SocialVisitsService from '@/services/SocialVisitsService';
import VisitLocationsService from '@/services/VisitLocationsService';

export default {
  name: 'PrintKioskItemsOrdered',
  components: {},
  data() {
    return {
      loading: true,
      isSocialVisits: false,
      date: '',
      items: [],
      filteredItems: [],
      selectedVisitLocationId: '',
      printing: false,
    };
  },
  computed: {
    ...mapState({
      user: state => state.auth.user,
      prison: state => state.prison.prison,
    }),
    visitLocationOptions() {
      const locations = [{ value: '', text: 'All' }];
      const mappedLocations = this.visitLocations.map(location => ({
        value: location._id,
        text: location.name,
      }));
      return locations.concat(mappedLocations);
    },
    selectedVisitLocation() {
      return this.visitLocations.find(l => l._id === this.selectedVisitLocationId)
    },
    itemsAvailableToPrint() {
      if (!this.filteredItems) return [];

      return Object.values(this.filteredItems)
        .flatMap(call => call.kiosk_items);
    },
  },
  async mounted() {
    try {
      await Promise.all([
        this.$store.dispatch('prison/getPrison'),
        this.getVisitLocations(),
      ]);
      this.loading = false;
    } catch (e) {
      console.error(e);
    }
  },
  methods: {
    async getVisitLocations() {
      try {
        const response = await VisitLocationsService.getVisitLocationsForPrison();
        this.visitLocations = response.data;
      } catch (e) {
        console.error('Error getting visit locations: ', e);
        const res = e.response;
        let errorText = 'Error fetching visit locations, please refresh and try again.';
        if (res && res.data.error) {
          errorText = res.data.error;
        }
        this.$toast.error(errorText);
        throw e;
      }
    },
    getItems() {
      this.items = [];

      if (this.isSocialVisits) {
        SocialVisitsService.getApprovedVisits(this.selectedVisitLocationId).then(res => {
          this.items = this.orderVisitsOrCalls(res.data).map(visit => {
            const bookedForDate = convertYearDayToDate(visit.booked_slot.year, visit.booked_slot.day);
            const bookedFor = `${moment(bookedForDate).format('DD/MM/YYYY')} ${visit.booked_slot.day_of_week} ${visit.booked_slot.slot_begin} - ${visit.booked_slot.slot_end}`;
            return {
              bookedFor,
              bookedForDate,
              ...visit,
            }
          });

          this.filteredItems = this.filteredVisitsOrCalls();
          this.loading = false;
        }).catch(() => {
          this.$toast.error('Could not get kiosk items ordered, please refresh and try again', {
            toastClassName: ['toast-std', 'warning-toast'],
          });
        });
      } else {
        VisitService.getApprovedVisits().then(res => {
          this.items = this.orderVisitsOrCalls(res.data).map(call => {
            const bookedFor = getVisitBookedForDate(call);
            return {
              bookedFor: bookedFor.bookedFor,
              bookedForDate: bookedFor.bookedForDate,
              ...call,
            };
          });

          this.filteredItems = this.filteredVisitsOrCalls();
          this.loading = false;
        }).catch(() => {
          this.$toast.error('Could not get kiosk items ordered, please refresh and try again', {
            toastClassName: ['toast-std', 'warning-toast'],
          });
        });
      }
    },
    orderVisitsOrCalls(visits) {
      if (!!visits && visits.length > 0) {
        return visits.sort((v1, v2) => {
          let v1Slot = v1.requested_booking_slot_1;
          let v2Slot = v2.requested_booking_slot_1;
          if (v1.booked_slot) {
            v1Slot = v1.booked_slot;
          }
          if (v2.booked_slot) {
            v2Slot = v2.booked_slot;
          }
          const v1StartDate = new Date(v1Slot.start_date_time);
          const v2StartDate = new Date(v2Slot.start_date_time);
          // Compare dates
          return v1StartDate.getTime() - v2StartDate.getTime();
        });
      }
      return [];
    },
    filteredVisitsOrCalls() {
      let filteredVisits = [...this.items];
      if (this.date) {
        const beforeDate = moment(this.date).format();
        filteredVisits = filteredVisits.filter(visit => {
          const sameDay = moment(visit.bookedForDate).isSame(beforeDate, 'day');
          return sameDay && visit.kiosk_items && visit.kiosk_items.length > 0;
        });
      }
      return filteredVisits;
    },
    print() {
      this.printing = true;
      this.$store.commit('verticalMenu/UPDATE_VERTICAL_MENU_COLLAPSED', true);
      window.addEventListener('afterprint', this.handleAfterPrint);
      setTimeout(() => {
        window.print();
      }, 500);
    },
    handleAfterPrint() {
      this.printing = false;
      this.$store.commit('verticalMenu/UPDATE_VERTICAL_MENU_COLLAPSED', false);
      window.removeEventListener('afterprint', this.handleAfterPrint);
    },
    clearFilters(clearLocation) {
      if (clearLocation) {
        this.selectedVisitLocationId = '';
      }
      this.date = '';
      this.visits = [];
    },
  },
};
</script>
